import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import ProfilePicture from "./profile-picture";
import ChangePhone from "./changePhone";
import {
  pickProfileReturnUrl,
  tryRedirectToDeeplinkOrRedirectUri,
} from "../../utils/profileReturnUrl";

function EditProfile() {
  const navigate = useNavigate();
  // USESTATE
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState("");
  const [openChangePhone, setOpenChangePhone] = useState(false);
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
  });

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  // MUTATION & QUERY
  const { mutate, isLoading, data, error } = useMutation((formData) => {
    return axiosPrivate.put(`/profile`, formData);
  });

  const {
    data: profile,
    isFetching,
    refetch,
  } = useQuery(
    ["edit_profile"],
    () => {
      return axiosPrivate.get(`/profile`);
    },
    {
      refetchOnWindowFocus: false,
      enabled: true,
    },
  );

  // HELPERS
  const onSubmit = (profileData) => {
    const payload = { ...profileData };

    if (
      payload.birth_date === undefined ||
      payload.birth_date === null ||
      payload.birth_date === "" ||
      payload.birth_date === "0001-01-01T00:00:00Z" ||
      (typeof payload.birth_date === "string" &&
        payload.birth_date.startsWith("0001-01-01"))
    ) {
      delete payload.birth_date;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(payload.birth_date)) {
      payload.birth_date = `${payload.birth_date}T00:00:00Z`;
    }

    if (payload.gender === "" || payload.gender == null) {
      delete payload.gender;
    }

    const dataToBeSent =
      file !== "" ? { ...payload, profile_picture: file } : payload;
    mutate(dataToBeSent);
  };

  const normalizeBirthDateForInput = (val) => {
    if (!val) return "";
    if (typeof val !== "string") return "";

    // Server uses "0001-01-01T00:00:00Z" as "unset" sentinel.
    if (val === "0001-01-01T00:00:00Z" || val.startsWith("0001-01-01"))
      return "";

    // Feed the date input with `YYYY-MM-DD` only.
    const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  };

  // USEEFFECT
  useEffect(() => {
    if (profile?.data?.data) {
      const d = profile.data.data;
      for (var key in d) {
        if (d.hasOwnProperty(key)) {
          const rawValue = d[key];
          const valueToSet =
            key === "birth_date"
              ? normalizeBirthDateForInput(rawValue)
              : rawValue;
          setValue(key, valueToSet);
        }
      }
      setValue("gender", d.gender ?? "");
      setValue("phone", d.phone ?? "");
    }
  }, [setValue, profile]);

  useEffect(() => {
    if (data) {
      enqueueSnackbar(`Succesfully updated.`, {
        variant: "success",
      });
      const next = pickProfileReturnUrl(params?.deeplink, params?.redirect_uri);
      if (next) {
        next.searchParams.set("edit_profile", "editProfile");
        window.location.href = next.toString();
      }
    }
  }, [data, enqueueSnackbar, params?.deeplink, params?.redirect_uri]);

  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Network Error!",
          {
            variant: "error",
          },
        );
      }
    }
  }, [enqueueSnackbar, error]);

  // RENDER
  return isFetching ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mt: 2,
        minHeight: "100vh",
      }}
    >
      <CircularProgress
        size={40}
        sx={{
          color: "secondary.main",
          marginTop: "-12px",
          marginLeft: "-12px",
        }}
      />
    </Box>
  ) : (
    <>
      <Dialog
        open={true}
        sx={{
          "& .MuiDialog-paper": {
            boxShadow: "0px 1px 1px 1px white",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "white",
          },
        }}
        PaperProps={{
          sx: {
            width: { lg: "25%", md: "40%", xs: "100%", sm: "45%" },
            height: "auto",
          },
        }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent
          sx={{
            px: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ProfilePicture
              page="edit_profile"
              edit={true}
              isOpen={open}
              setIsOpen={setOpen}
              setFile={setFile}
              refetch={refetch}
              imageSrc={
                file !== "" ? file : profile?.data?.data?.profile_picture
              }
            />
            <Controller
              name="first_name"
              control={control}
              rules={{
                required: "First Name is required",
              }}
              render={({ field }) => (
                <TextField
                  label="First Name"
                  autoComplete="first_name"
                  margin="normal"
                  fullWidth
                  error={!!errors?.first_name}
                  helperText={
                    errors?.first_name ? errors.first_name.message : null
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              rules={{
                required: "Middle Name is required",
              }}
              render={({ field }) => (
                <TextField
                  label="Middle Name"
                  variant="outlined"
                  autoComplete="middle_name"
                  margin="normal"
                  fullWidth
                  error={!!errors?.middle_name}
                  helperText={
                    errors?.middle_name ? errors.middle_name.message : null
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: "Last Name is required",
              }}
              render={({ field }) => (
                <TextField
                  label="Last Name"
                  variant="outlined"
                  autoComplete="last_name"
                  margin="normal"
                  fullWidth
                  error={!!errors?.last_name}
                  helperText={
                    errors?.last_name ? errors.last_name.message : null
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Gender"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  select
                  size="small"
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  {...field}
                >
                  <MenuItem value="">
                    <em>Select gender</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="PREFER_NOT_TO_SAY">Prefer Not To Say</MenuItem>
                </TextField>
              )}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                bgcolor: "#e9e9e9",
                width: "100%",
                my: 1,
                py: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "medium",
                  textAlign: "center",
                  ml: 2,
                  color: profile?.data?.data?.phone
                    ? "text.primary"
                    : "text.disabled",
                }}
              >
                {profile?.data?.data?.phone}
              </Typography>
              <Grid container justifyContent="flex-end">
                <Box>
                  <Button
                    onClick={() => setOpenChangePhone(true)}
                    sx={{
                      bgcolor: "#2a2a2a",
                      borderColor: "#2a2a2a",
                      borderRadius: 5,
                      mx: 1,
                    }}
                    size="small"
                    variant="outlined"
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "primary.paper",
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      {profile?.data?.data?.phone
                        ? "Change Phone"
                        : "Add Phone"}
                    </Typography>
                  </Button>
                  <ChangePhone
                    open={openChangePhone}
                    phone={profile?.data?.data?.phone}
                    refetch={refetch}
                    setOpen={setOpenChangePhone}
                  />
                </Box>
              </Grid>
            </Box>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Email"
                  variant="outlined"
                  autoComplete="email"
                  margin="normal"
                  fullWidth
                  error={!!errors?.email}
                  helperText={errors?.email ? errors.email.message : null}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  {...field}
                  disabled={!!profile?.data?.data?.email}
                />
              )}
            />
            <Controller
              name="birth_date"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Birth Date"
                  type="date"
                  margin="normal"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  {...field}
                />
              )}
            />
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Location"
                  margin="normal"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "secondary.main",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "secondary.main",
                    },
                  }}
                  placeholder="e.g. Addis Ababa, Ethiopia"
                  {...field}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "stretch",
              width: "100%",
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              size="large"
              type="button"
              sx={{
                bgcolor: "#e4e4e4",
                ":hover": { bgcolor: "#e4e4e4" },
                textTransform: "none",
                borderRadius: 6,
              }}
              onClick={() => {
                if (
                  tryRedirectToDeeplinkOrRedirectUri(
                    params?.deeplink,
                    params?.redirect_uri
                  )
                ) {
                  return;
                }
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate("/");
                }
              }}
            >
              <Typography
                sx={{ color: "black", fontSize: 14, fontWeight: "bold" }}
              >
                Back
              </Typography>
            </Button>
            <Button
              fullWidth
              size="large"
              sx={{
                bgcolor: "black",
                ":hover": { bgcolor: "black" },
                textTransform: "none",
                borderRadius: 6,
              }}
              type="submit"
              disabled={isLoading}
            >
              <Typography
                sx={{ color: "#fff", fontSize: 14, fontWeight: "bold" }}
              >
                Save
              </Typography>
              {isLoading && data && (
                <CircularProgress
                  size={20}
                  sx={{
                    color: "#fff",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
export default EditProfile;
